import * as React from 'react'

import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Field, Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import Loading from 'modules/core/components/Loading'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import i18n from 'modules/core/lib/i18n'

import type { TimeOptionsCollection } from 'modules/automations/store/collections/timeOptions'
import { timeOptions } from 'modules/automations/store/collections/timeOptions'

interface Props {
  form: Form
}

const useSubjects = createComputed((timeOptions: TimeOptionsCollection) => {
  return timeOptions.map((o) => {
    return {
      label: o.attributes.label,
      value: o.attributes.id,
    }
  })
})

const useAttributes = createComputed((timeOptions: TimeOptionsCollection, subject: string) => {
  const option = timeOptions.get(subject)

  if (!subject || !option) return []

  const hashmapOptionsGroups = option.attributes.attributes.reduce<Record<string, SelectOption[]>>(
    (map, attribute) => {
      const attribute_formated = {
        label: attribute.label,
        value: attribute.id,
      }

      const selectOptions = map[attribute.subcategory] || []
      selectOptions.push(attribute_formated)
      map[attribute.subcategory] = selectOptions

      return map
    },
    {}
  )

  return hashmapOptionsGroups
})

const useTimeUnits = createComputed(
  (timeOptions: TimeOptionsCollection, subject: string, attribute: string) => {
    const option = timeOptions.get(subject)
    const selectedAttribute = option?.attributes.attributes.find((a) => a.id === attribute)

    if (!selectedAttribute) return []

    return selectedAttribute.time_units.map((o) => {
      return {
        label: o.label,
        value: o.id,
      }
    })
  }
)

const Selects = ({ form }: Props) => {
  const { loading } = useAsyncData(() => timeOptions.fetch(), [])
  const [subject, setSubject] = React.useState(form.get('subject').value)
  const [attribute, setAttribute] = React.useState(form.get('subject_attr').value)
  const subjects = useSubjects(timeOptions).get()
  const attributes = useAttributes(timeOptions, subject).get()
  const timeUnits = useTimeUnits(timeOptions, subject, attribute).get()

  const updateSubject = useUnsafeCallback(
    (field: Field) => {
      setSubject(field.value)
      setAttribute('')
      form.setValues({
        subject_attr: '',
        time_unit: '',
      })
    },
    [setSubject, setAttribute, form]
  )

  const updateAttribute = useUnsafeCallback(
    (field: Field) => {
      setAttribute(field.value)
      form.setValues({
        time_unit: '',
      })
    },
    [setAttribute, form]
  )

  if (loading) return <Loading />

  return (
    <>
      <FormRow multi>
        <Select
          none={i18n.t('common.select_one')}
          field={form.get('subject')}
          label={i18n.t('automations.actions.time.subject')}
          options={subjects}
          onChange={updateSubject}
        />
        <Select
          none={i18n.t('common.select_one')}
          field={form.get('subject_attr')}
          label={i18n.t('automations.actions.time.subject_attr')}
          options={attributes}
          onChange={updateAttribute}
          readonly={attributes.length === 0}
        />
      </FormRow>
      <FormRow multi>
        <Select
          none={i18n.t('common.select_one')}
          field={form.get('time_unit')}
          label={i18n.t('automations.actions.time.time_unit')}
          options={timeUnits}
          readonly={timeUnits.length === 0}
        />
        <Text
          label={i18n.t('automations.actions.time.amount_time')}
          field={form.get('amount_time')}
          readonly={timeUnits.length === 0}
        />
      </FormRow>
    </>
  )
}

export default observer(Selects)
